import { CloseCircleOutlined, DoubleRightOutlined } from '@ant-design/icons'
import { useDebounceFn, usePrevious } from 'ahooks'
import { Spin } from 'antd'
import cls from 'classnames'
import React from 'react'
import { useHistory, useRouteMatch } from 'react-router-dom'

import {
  AnnualReportIdentity,
  RequestedAnnualReportFragment,
  useGetUserAnnualReportQuery,
  useGetViewerQuery,
} from '@/generated/graphql'

import { useIsSystemAdmin } from '../admin/admin.hooks'
import s from './annual-report.module.less'
import { drawAnnualReportPage, getAnnualReportBackgroundImageUrl, loadFont } from './annual-report.util'

export const AnnualReportContainer = () => {
  // annual report init 值
  const fontName = 'ARFont'
  const backgroundWidth = 640
  const backgroundHeight = 1008
  const matches = useRouteMatch('/annual-report/:email')
  const email = matches?.params?.['email']

  const history = useHistory()
  const isSystemAdmin = useIsSystemAdmin()
  const [backgroundCount, setBackgroundCount] = React.useState(0)
  const [index, setIndex] = React.useState(0)
  const [startTouch, setStartTouch] = React.useState<{ startX: number; startY: number }>({ startX: 0, startY: 0 })
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [bodyOverflow, setBodyOverflow] = React.useState('unset')
  const [canvasUrls, setCanvasUrls] = React.useState<{ index: number; url: string; hasContent: boolean }[]>([])
  const [annualReport, setAnnualReport] = React.useState<RequestedAnnualReportFragment>()
  const previous = usePrevious(annualReport)

  const { data: viewerData } = useGetViewerQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })

  const { data } = useGetUserAnnualReportQuery({
    variables: {
      userName: email || viewerData?.viewer.email.split('@')[0],
    },
  })

  const imageContents = canvasUrls.filter((url) => url.hasContent)
  const imageCount = imageContents.length

  const isLoading = canvasUrls.length !== backgroundCount || backgroundCount === 0

  const { run: onTouchStartFn } = useDebounceFn(
    (e) => {
      const startX = e.touches[0].pageX
      const startY = e.touches[0].pageY
      setStartTouch({ startX, startY })
    },
    { wait: 200 }
  )

  const { run: onTouchEndFn } = useDebounceFn(
    (e) => {
      const distanceX = e.changedTouches[0].pageX - startTouch.startX
      const distanceY = e.changedTouches[0].pageY - startTouch.startY
      // 判定为竖直方向
      if (Math.abs(distanceY) > Math.abs(distanceX)) {
        if (distanceY > 30) {
          setPageDown()
        } else if (distanceY < -30) {
          setPageUp()
        }
      }
    },
    { wait: 200 }
  )

  const { run: onWheelFn } = useDebounceFn(
    (e) => {
      e.stopPropagation()
      if (e.nativeEvent.deltaY < 0) {
        // down
        setPageDown()
      } else {
        // up
        setPageUp()
      }
    },
    { wait: 200 }
  )

  const setPageDown = () => {
    setIndex((prev) => {
      if (prev === 0) {
        return prev
      }
      return prev - 1
    })
  }
  const setPageUp = () => {
    setIndex((prev) => {
      if (prev === imageCount - 1) {
        return prev
      }
      return prev + 1
    })
  }

  React.useEffect(() => {
    // 设置 overflow 防止滑动操作出现留白
    setBodyOverflow(document.body.style.overflow)
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = bodyOverflow
    }
  }, [])

  React.useEffect(() => {
    if (data) {
      setAnnualReport(data.user.annualReport)
    }
  }, [data])

  React.useEffect(() => {
    // 修复开发环境热加载失败
    let flag = false
    if (!previous && annualReport) {
      // 加载字体
      const fontFace = loadFont(fontName)
      fontFace.loaded.then(() => {
        const identityImageOffset = (() => {
          switch (annualReport.identity) {
            case AnnualReportIdentity.Content:
              return 0
            case AnnualReportIdentity.Atmosphere:
              return 1
            case AnnualReportIdentity.CommonLanding:
              return 2
            case AnnualReportIdentity.Social:
              return 3
            case AnnualReportIdentity.InActivity:
              return 4
            default:
              return 5
          }
        })()

        // Content, Atmosphere 身份多一页领奖页
        const imageCount = [AnnualReportIdentity.Content, AnnualReportIdentity.Atmosphere].includes(
          annualReport.identity
        )
          ? 11
          : 10
        setBackgroundCount(imageCount)
        // 绘制 canvas
        for (let i = 0; i < imageCount; i++) {
          const isIdentityImage = i === 9
          const isEndImage = i === 10

          const index = isIdentityImage ? identityImageOffset + i : isEndImage ? 15 : i
          const image = new Image()
          image.onload = () => {
            const canvas = document.createElement('canvas')
            canvas.width = backgroundWidth
            canvas.height = backgroundHeight
            const ctx = canvas.getContext('2d')
            const hasContent = drawAnnualReportPage(ctx!, image, fontName, index, annualReport, data!.user.name)
            if (!flag && canvasUrls.length < imageCount) {
              setCanvasUrls((prev) => [...prev, { index: Number(image.id), hasContent, url: canvas.toDataURL() }])
            }
          }
          image.crossOrigin = 'anonymous'
          image.id = i.toString()
          image.src = getAnnualReportBackgroundImageUrl(index)
        }
      })
      return () => {
        flag = true
      }
    }
  }, [annualReport])

  if (!isSystemAdmin) {
    return null
  }

  const content = isLoading ? (
    <Spin tip='生成年度飞行报告中' delay={300} />
  ) : (
    <>
      {index !== imageCount - 1 && (
        <div className={s.topArrow}>
          <DoubleRightOutlined style={{ fontSize: 20, color: 'white' }} />
        </div>
      )}
      {/* {index !== 0 && (
    <div className={s.bottomArrow}>
      <DoubleRightOutlined style={{ fontSize: 20, color: 'white' }} />
    </div>
  )} */}
      {index === imageCount - 1 && (
        <div className={s.close} onClick={() => history.push('/')}>
          <CloseCircleOutlined style={{ fontSize: 20, color: 'white' }} />
        </div>
      )}
      {imageContents
        .sort((a, b) => a.index - b.index)
        .map((url, i) => (
          <img
            key={i}
            src={url.url}
            crossOrigin='anonymous'
            className={cls({
              [s.out]: i === imageCount - 1 ? undefined : index > i,
              [s.in]: index === i,
              [s.before]: i === 0 ? undefined : index < i,
            })}
          />
        ))}
    </>
  )
  return (
    <div
      ref={containerRef}
      className={s.container}
      style={{
        height: window.innerHeight,
        maxHeight: window.innerHeight,
      }}
      onScroll={(e) => e.preventDefault()}
      onTouchStart={onTouchStartFn}
      onTouchEnd={onTouchEndFn}
      onTouchMove={(e) => e.preventDefault()}
      onWheel={onWheelFn}
    >
      {content}
    </div>
  )
}
